import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../firebase.ts';
import AdminLogin from './AdminLogin.tsx';
import AdminHeader, { StoreInfo, buildStoreList } from './AdminHeader.tsx';
import AdminMenuTypeSelector from './AdminMenuTypeSelector.tsx';
import AdminFeatureSelector, { AdminFeature } from './AdminFeatureSelector.tsx';
import AdminEmptyState from './AdminEmptyState.tsx';
import CartMessageEditor from './CartMessageEditor.tsx';
import PopupAlertEditor from './PopupAlertEditor.tsx';
import MenuAuditTable from './MenuAuditTable.tsx';
import StoreHoursEditor from './StoreHoursEditor.tsx';
import TemporaryClosuresManager from './TemporaryClosuresManager.tsx';
import StoreChangesConfirmation from './StoreChangesConfirmation.tsx';
import ScheduledDealsQueue from './ScheduledDealsQueue.tsx';
import ImageUploadManager from './ImageUploadManager.tsx';
import DeepLinkConfigurator from './DeepLinkConfigurator.tsx';
import RegulatoryLinkInput from './RegulatoryLinkInput.tsx';
import HomeCollectionsEditor from './HomeCollectionsEditor.tsx';
import { loadStoreProducts } from '../services/AdminMenuService.ts';
import { MenuProduct } from '../services/MenuService.ts';
import { getDealMemo, saveDealMemo, getAllStoreDeals, deleteScheduledDeal, reorderDeals, createScheduledImageDeal, updateScheduledImageDeal, pauseScheduledDeal, updateScheduledDealSeries, deleteScheduledDealSeries, DealMemoData, StoreDealsData, ScheduledDealData } from '../services/dealsApiService.ts';
import SeriesScopeDialog, { SeriesScope } from './SeriesScopeDialog.tsx';
import { uploadMobileImage, uploadDesktopImage } from '../services/imageUploadService.ts';
import ProductPicker from './ProductPicker.tsx';
import { DeepLinkConfig, generateDeepLinkUrl, Environment } from '../services/DeepLinkService.ts';
import { MENU_ID_TO_STORE, STORE_MENU_CONFIG } from '../config/storeMenuConfig.ts';
import {
  getStoreHoursForAdmin,
  saveStoreHours,
  updateTemporaryClosures,
  StoreHoursData,
  WeeklyHours,
  TemporaryClosure
} from '../services/storeHoursApiService.ts';
import {
  getDateInputValue,
  getTimeInputValue,
  combineDateTimeInputs,
  getTimezoneAbbreviation,
  isBrowserTimzoneDifferent,
  convertUtcToStoreTimezone,
  isDateInPast,
  logTimezoneDebug
} from '../utils/timezoneUtils.ts';
import './AdminPortalRedesign.css';

const isTestMode = process.env.REACT_APP_CYPRESS_TEST_MODE === 'true' &&
                   window.location.hostname === 'localhost';

const AdminPortalRedesign: React.FC = () => {
  // Authentication state
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Store selection state (new 3-step flow)
  const [selectedStore, setSelectedStore] = useState<StoreInfo | null>(null);
  const [selectedMenuType, setSelectedMenuType] = useState<'med' | 'rec' | null>(null);
  const [selectedFeature, setSelectedFeature] = useState<AdminFeature>('deals');

  // Content/data state
  const [loadingContent, setLoadingContent] = useState(false);
  const [storeDealsData, setStoreDealsData] = useState<StoreDealsData | null>(null);
  const [storeHoursData, setStoreHoursData] = useState<StoreHoursData | null>(null);
  const [menuAuditProducts, setMenuAuditProducts] = useState<MenuProduct[]>([]);

  // Store hours editing state
  const [hours, setHours] = useState<WeeklyHours | null>(null);
  const [originalHours, setOriginalHours] = useState<WeeklyHours | null>(null);
  const [temporaryClosures, setTemporaryClosures] = useState<TemporaryClosure[]>([]);
  const [originalTemporaryClosures, setOriginalTemporaryClosures] = useState<TemporaryClosure[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [saving, setSaving] = useState(false);

  // Deals state
  const dealType = 'image' as const;
  const [dealTitle, setDealTitle] = useState<string>('');
  const [mobileImageUrl, setMobileImageUrl] = useState<string | null>(null);
  const [desktopImageUrl, setDesktopImageUrl] = useState<string | null>(null);
  const [mobileImageFile, setMobileImageFile] = useState<File | null>(null);
  const [desktopImageFile, setDesktopImageFile] = useState<File | null>(null);
  const [bannerLinkType, setBannerLinkType] = useState<'none' | 'filter' | 'product' | 'multi-product' | 'special' | 'regulatory'>('none');
  const [deepLinkConfig, setDeepLinkConfig] = useState<DeepLinkConfig | null>(null);
  const [regulatoryLink, setRegulatoryLink] = useState<string | null>(null);

  // Multi-product picker state. Loaded lazily when the user selects "Multiple Products".
  const [multiProductIds, setMultiProductIds] = useState<string[]>([]);
  const [multiProductCatalog, setMultiProductCatalog] = useState<MenuProduct[]>([]);
  const [multiProductLoading, setMultiProductLoading] = useState(false);
  const [viewingScheduledDeal, setViewingScheduledDeal] = useState<ScheduledDealData | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Series-scope dialog state. When the user edits or deletes a recurring instance,
  // we hold the action here until they pick this/future/all.
  const [seriesScopeDialog, setSeriesScopeDialog] = useState<
    | { action: 'delete'; deal: ScheduledDealData }
    | { action: 'edit'; deal: ScheduledDealData; commit: (scope: SeriesScope) => Promise<void> }
    | null
  >(null);


  // Scheduling state
  const [schedulingEnabled, setSchedulingEnabled] = useState(false);
  const [scheduleMode, setScheduleMode] = useState<'one-time' | 'recurring'>('one-time');
  const [startDate, setStartDate] = useState<string>('');
  const [startTime, setStartTime] = useState<string>('00:00'); // Default: midnight
  const [endDate, setEndDate] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('23:59'); // Default: end of day
  const [customTimesEnabled, setCustomTimesEnabled] = useState(false); // When false, use full-day defaults
  // Note: Time inputs use 24-hour format (HH:MM), no AM/PM needed
  const [targetMenuTypes, setTargetMenuTypes] = useState<('medical' | 'recreational')[]>(['medical', 'recreational']);

  // Recurring schedule state (used when scheduleMode === 'recurring')
  const [recurringDays, setRecurringDays] = useState<number[]>([]); // 0=Sun, 1=Mon, etc.
  const [recurringWeeks, setRecurringWeeks] = useState(4); // Number of weeks to generate
  const [customStartDateEnabled, setCustomStartDateEnabled] = useState(false); // When false, use next available occurrence
  const [recurringStartDate, setRecurringStartDate] = useState<string>(''); // Custom start date if enabled

  // Date drift for recurring schedules (allows banner to extend beyond anchor day)
  const [dateDriftEnabled, setDateDriftEnabled] = useState(false);
  const [driftStartHours, setDriftStartHours] = useState(0); // 0-12 hours before anchor day
  const [driftEndHours, setDriftEndHours] = useState(0);     // 0-12 hours after anchor day

  // Build store list
  const stores = useMemo(() => buildStoreList(), []);

  // Auth listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Determine if store needs menu type selection
  // For deals feature, we skip menu type selection - users can select med/rec/both in the deal editor
  const needsMenuTypeSelection = useMemo(() => {
    if (!selectedStore) return false;
    // Skip menu type selection for deals - handled in deal editor
    if (selectedFeature === 'deals') return false;
    // Skip menu type selection for popup alerts - they are store-level
    if (selectedFeature === 'popup-alerts') return false;
    return selectedStore.hasMenuTypes.med && selectedStore.hasMenuTypes.rec && !selectedMenuType;
  }, [selectedStore, selectedMenuType, selectedFeature]);

  // Get the current document ID based on selection
  // For deals feature, we use the first available menu ID as the "primary" for UI purposes
  const currentDocId = useMemo(() => {
    if (!selectedStore) return null;
    // For deals, use first available menu (prefer medical) even without menu type selection
    if (selectedFeature === 'deals' && !selectedMenuType) {
      return selectedStore.documentIds.med || selectedStore.documentIds.rec || null;
    }
    if (!selectedMenuType) return null;
    return selectedStore.documentIds[selectedMenuType] || null;
  }, [selectedStore, selectedMenuType, selectedFeature]);

  // Get the store path for the selected store (e.g., "Maryland/Olney")
  const currentStorePath = useMemo(() => {
    if (!selectedStore) return null;
    return `${selectedStore.stateFull}/${selectedStore.id}`;
  }, [selectedStore]);

  // Get store context from legacy menu ID (for deep link configurator)
  const getStoreContext = (legacyMenuId: string) => {
    const storeInfo = MENU_ID_TO_STORE[legacyMenuId];
    if (!storeInfo) {
      console.error('[AdminPortalRedesign] No store info found for legacy menu ID:', legacyMenuId);
      return null;
    }

    const storeConfigKey = `${storeInfo.state}/${storeInfo.store}`;
    const storeConfig = STORE_MENU_CONFIG[storeConfigKey];
    if (!storeConfig) {
      console.error('[AdminPortalRedesign] No store config found for:', storeConfigKey);
      return null;
    }

    const menuConfig = storeConfig[storeInfo.type];
    if (!menuConfig) {
      console.error('[AdminPortalRedesign] No menu config found for type:', storeInfo.type);
      return null;
    }

    return {
      storeId: storeConfigKey,
      menuType: storeInfo.type,
      retailerId: menuConfig.menuId,
      legacyMenuId: legacyMenuId
    };
  };

  // Helper to fetch specials for a given legacy menu ID
  const fetchSpecialsForMenu = async (legacyMenuId: string): Promise<{ id: string; name: string; type: string }[]> => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/deals/${legacyMenuId}/all`);
      if (!response.ok) return [];
      const data = await response.json();
      return data.success && Array.isArray(data.data) ? data.data : [];
    } catch {
      return [];
    }
  };

  // Helper to get paired menu IDs for a store (medical and recreational)
  const getPairedMenuIds = (storePath: string): { medical?: string; recreational?: string } => {
    const storeConfig = STORE_MENU_CONFIG[storePath];
    if (!storeConfig) return {};

    return {
      medical: storeConfig.medical?.legacyMenuId,
      recreational: storeConfig.recreational?.legacyMenuId
    };
  };

  // Helper to combine date and time (24-hour format) into a Date object
  // The HTML time input gives us HH:MM in 24-hour format directly
  const combineDateTime = (date: string, time: string, storeId: string): Date | null => {
    if (!date || !time) return null;
    return combineDateTimeInputs(date, time, storeId);
  };

  // Generate recurring schedule dates based on selected days and weeks
  // Supports overnight schedules (end time < start time = ends next day)
  // Supports date drift (extending banner before/after anchor day by up to 12 hours)
  const generateRecurringDates = (
    baseStartTime: string,
    baseEndTime: string,
    days: number[],
    weeks: number,
    storeId: string,
    customStartDate?: string, // Optional: start from this date instead of today
    driftConfig?: {
      startHoursBefore: number;  // 0-12 hours to shift start earlier
      endHoursAfter: number;     // 0-12 hours to shift end later
    }
  ): Array<{ startDateTime: Date; endDateTime: Date }> => {
    const dates: Array<{ startDateTime: Date; endDateTime: Date }> = [];

    // Parse custom start date with validation
    let baseDate: Date;
    if (customStartDate) {
      const parsed = new Date(customStartDate + 'T00:00:00');
      // Check if date is valid
      if (isNaN(parsed.getTime())) {
        // Invalid date - fall back to today
        baseDate = new Date();
      } else {
        baseDate = parsed;
      }
    } else {
      baseDate = new Date();
    }
    baseDate.setHours(0, 0, 0, 0);

    // Check if this is an overnight schedule (end time < start time)
    const [startHours] = baseStartTime.split(':').map(Number);
    const [endHours] = baseEndTime.split(':').map(Number);
    const isOvernight = endHours < startHours;

    // Track generated dates to avoid duplicates
    const generatedDateKeys = new Set<string>();

    for (let week = 0; week < weeks; week++) {
      for (const dayOfWeek of days) {
        const startDate = new Date(baseDate);
        const currentDay = startDate.getDay();
        let daysToAdd = dayOfWeek - currentDay;

        // For the first week, if the day has already passed or is today (and no custom date), skip to next week
        // If custom date is set, include if the day matches
        if (week === 0 && daysToAdd < 0) {
          daysToAdd += 7;
        } else if (week === 0 && daysToAdd === 0 && !customStartDate) {
          // Today's day matches but no custom date set - skip to next week
          daysToAdd += 7;
        }
        daysToAdd += week * 7;
        startDate.setDate(startDate.getDate() + daysToAdd);

        const startDateStr = startDate.toISOString().split('T')[0];

        // For overnight deals, end date is the next day
        const endDate = new Date(startDate);
        if (isOvernight) {
          endDate.setDate(endDate.getDate() + 1);
        }
        const endDateStr = endDate.toISOString().split('T')[0];

        // Create a unique key for this date combination to avoid duplicates
        const dateKey = `${startDateStr}_${endDateStr}`;
        if (generatedDateKeys.has(dateKey)) {
          continue; // Skip duplicates
        }
        generatedDateKeys.add(dateKey);

        const startDt = combineDateTime(startDateStr, baseStartTime, storeId);
        const endDt = combineDateTime(endDateStr, baseEndTime, storeId);

        if (startDt && endDt) {
          // Apply date drift if configured (extends banner before/after anchor day)
          if (driftConfig) {
            if (driftConfig.startHoursBefore > 0) {
              startDt.setTime(startDt.getTime() - (driftConfig.startHoursBefore * 60 * 60 * 1000));
            }
            if (driftConfig.endHoursAfter > 0) {
              endDt.setTime(endDt.getTime() + (driftConfig.endHoursAfter * 60 * 60 * 1000));
            }
          }
          dates.push({ startDateTime: startDt, endDateTime: endDt });
        }
      }
    }

    dates.sort((a, b) => a.startDateTime.getTime() - b.startDateTime.getTime());
    return dates;
  };

  // Fetch deals for a given store
  const fetchDeals = async (store: StoreInfo) => {
    const docId = store.documentIds.med || store.documentIds.rec;
    if (!docId) return;

    setLoadingContent(true);
    try {
      const allDeals = await getAllStoreDeals(docId);
      setStoreDealsData(allDeals);
    } catch (error) {
      console.error('Error loading deals:', error);
    } finally {
      setLoadingContent(false);
    }
  };

  // Handle store selection
  const handleStoreSelect = (store: StoreInfo) => {
    setSelectedStore(store);
    setSelectedMenuType(null);
    setStoreDealsData(null);
    setStoreHoursData(null);
    setMenuAuditProducts([]);

    // Auto-select menu type if only one is available
    if (store.hasMenuTypes.med && !store.hasMenuTypes.rec) {
      setSelectedMenuType('med');
    } else if (store.hasMenuTypes.rec && !store.hasMenuTypes.med) {
      setSelectedMenuType('rec');
    }

    // Fetch deals immediately (default feature is deals)
    if (selectedFeature === 'deals') {
      fetchDeals(store);
    }
  };

  // Handle menu type selection
  const handleMenuTypeSelect = (type: 'med' | 'rec') => {
    setSelectedMenuType(type);
  };

  // Lazy-load the product catalog the first time the user opens the multi-product picker.
  // We don't load on store-change so the deals page stays cheap when the GM never picks this option.
  useEffect(() => {
    if (bannerLinkType !== 'multi-product') return;
    if (!currentDocId) return;
    if (multiProductCatalog.length > 0) return;
    const ctx = getStoreContext(currentDocId);
    if (!ctx?.retailerId) return;

    let cancelled = false;
    setMultiProductLoading(true);
    loadStoreProducts(ctx.retailerId)
      .then(products => { if (!cancelled) setMultiProductCatalog(products); })
      .catch(error => console.error('Error loading products for multi-product picker:', error))
      .finally(() => { if (!cancelled) setMultiProductLoading(false); });

    return () => { cancelled = true; };
  }, [bannerLinkType, currentDocId, multiProductCatalog.length]);

  // Load non-deals content when store + menu type are selected
  useEffect(() => {
    if (!selectedStore || !selectedMenuType) return;
    if (selectedFeature === 'deals') return;

    const docId = selectedStore.documentIds[selectedMenuType];
    if (!docId) return;

    const loadContent = async () => {
      setLoadingContent(true);
      try {
        if (selectedFeature === 'store-hours') {
          const data = await getStoreHoursForAdmin(docId);
          setStoreHoursData(data);
          setHours(data?.hours || null);
          setOriginalHours(data?.hours || null);
          setTemporaryClosures(data?.temporaryClosures || []);
          setOriginalTemporaryClosures(data?.temporaryClosures || []);
        } else if (selectedFeature === 'menu-audit') {
          const storeConfigKey = `${selectedStore.stateFull}/${selectedStore.id}`;
          const storeConfig = STORE_MENU_CONFIG[storeConfigKey];
          const menuConfig = storeConfig?.[selectedMenuType === 'med' ? 'medical' : 'recreational'];
          if (menuConfig?.menuId) {
            const products = await loadStoreProducts(menuConfig.menuId);
            setMenuAuditProducts(products);
          }
        } else if (selectedFeature === 'home-collections') {
          // HomeCollectionsEditor handles its own data loading
        }
      } catch (error) {
        console.error('Error loading content:', error);
      } finally {
        setLoadingContent(false);
      }
    };

    loadContent();
  }, [selectedStore, selectedMenuType, selectedFeature]);

  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      setSelectedStore(null);
      setSelectedMenuType(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Handle feature change
  const handleFeatureChange = (feature: AdminFeature) => {
    setSelectedFeature(feature);
    // Reset menu type when switching to features that don't use it
    if (feature === 'deals') {
      setSelectedMenuType(null);
      // Fetch deals directly instead of relying on useEffect
      if (selectedStore) {
        fetchDeals(selectedStore);
      }
    }
    if (feature === 'popup-alerts') {
      setSelectedMenuType(null);
    }
    setStoreHoursData(null);
    setMenuAuditProducts([]);
  };

  // Save store hours
  const handleSaveHours = async () => {
    if (!originalHours || !hours) return;

    const hoursChanged = JSON.stringify(originalHours) !== JSON.stringify(hours);
    const closuresChanged = JSON.stringify(originalTemporaryClosures) !== JSON.stringify(temporaryClosures);

    if (!hoursChanged && !closuresChanged) {
      alert('No changes to save.');
      return;
    }

    setShowConfirmation(true);
  };

  const handleConfirmSave = async () => {
    if (!selectedStore || !selectedMenuType || !user || !hours) return;

    setSaving(true);
    setShowConfirmation(false);

    try {
      // Use Dutchie menu ID for store hours API
      const storeId = selectedStore.documentIds[selectedMenuType];
      if (!storeId) return;

      const hoursChanged = JSON.stringify(originalHours) !== JSON.stringify(hours);
      const closuresChanged = JSON.stringify(temporaryClosures) !== JSON.stringify(originalTemporaryClosures);

      if (hoursChanged) {
        await saveStoreHours(storeId, hours);
      }

      if (closuresChanged) {
        await updateTemporaryClosures(storeId, temporaryClosures);
      }

      // Update originals
      setOriginalHours(hours);
      setOriginalTemporaryClosures(temporaryClosures);

      alert('Store hours saved successfully!');
    } catch (error) {
      console.error('Error saving store hours:', error);
      alert(`Failed to save hours: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="admin-portal-redesign-loading">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Not authenticated
  if (!user && !isTestMode) {
    return (
      <>
        <Helmet>
          <title>Admin Login - Private</title>
          <meta name="robots" content="noindex, nofollow, noarchive, nosnippet" />
        </Helmet>
        <AdminLogin />
      </>
    );
  }

  // Render content based on selection state
  const renderContent = () => {
    // No store selected - show empty state
    if (!selectedStore) {
      return <AdminEmptyState />;
    }

    // Store selected but needs menu type
    if (needsMenuTypeSelection) {
      return (
        <AdminMenuTypeSelector
          selectedType={selectedMenuType}
          onSelectType={handleMenuTypeSelect}
          storeName={`${selectedStore.city}, ${selectedStore.state}`}
        />
      );
    }

    // Loading content (skip for deals if we have cached data to show, skip for popup alerts which load independently)
    if (loadingContent && !(selectedFeature === 'deals' && storeDealsData) && selectedFeature !== 'popup-alerts') {
      return (
        <div className="admin-content-loading">
          <div className="loading-spinner"></div>
          <p>Loading {selectedFeature}...</p>
        </div>
      );
    }

    // Feature content
    switch (selectedFeature) {
      case 'deals':
        return (
          <div className="admin-feature-content">
            {loadingContent && !storeDealsData && (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: 'rgba(0, 68, 59, 0.6)' }}>
                Loading deals...
              </div>
            )}
            {storeDealsData && (
              <ScheduledDealsQueue
                scheduledDeals={storeDealsData.scheduledDeals}
                storeId={currentDocId || ''}
                onViewDeal={(deal) => {
                  setViewingScheduledDeal(deal);
                  setIsEditing(true);
                  setDealTitle(deal.title || '');
                  if (deal.imageUrls) {
                    setMobileImageUrl(deal.imageUrls.mobile);
                    setDesktopImageUrl(deal.imageUrls.desktop);
                  } else {
                    setMobileImageUrl(null);
                    setDesktopImageUrl(null);
                  }
                  setMobileImageFile(null);
                  setDesktopImageFile(null);
                  setDeepLinkConfig(deal.deepLink || null);
                  setRegulatoryLink(deal.regulatoryLink || null);
                  setMultiProductIds(deal.deepLink?.productIds || []);
                  // Determine banner link type
                  if (deal.regulatoryLink) {
                    setBannerLinkType('regulatory');
                  } else if (deal.deepLink) {
                    if (deal.deepLink.linkType === 'filter') setBannerLinkType('filter');
                    else if (deal.deepLink.linkType === 'special') setBannerLinkType('special');
                    else if (deal.deepLink.linkType === 'product') setBannerLinkType('product');
                    else if (deal.deepLink.linkType === 'multi-product') setBannerLinkType('multi-product');
                    else setBannerLinkType('none');
                  } else {
                    setBannerLinkType('none');
                  }
                  // Populate scheduling times (using 24-hour format from getTimeInputValue)
                  if (deal.startDateTime && deal.endDateTime && currentDocId) {
                    const startDateInStore = convertUtcToStoreTimezone(deal.startDateTime, currentDocId);
                    const endDateInStore = convertUtcToStoreTimezone(deal.endDateTime, currentDocId);
                    setStartDate(getDateInputValue(startDateInStore, currentDocId));
                    setEndDate(getDateInputValue(endDateInStore, currentDocId));
                    setStartTime(getTimeInputValue(startDateInStore, currentDocId));
                    setEndTime(getTimeInputValue(endDateInStore, currentDocId));
                    setSchedulingEnabled(true);
                  }
                }}
                onDeleteDeal={async (deal) => {
                  // Recurring series? Surface the scope choice (this / future / all).
                  if (deal.recurringSeriesId) {
                    setSeriesScopeDialog({ action: 'delete', deal });
                    return;
                  }
                  if (window.confirm('Delete this scheduled deal?')) {
                    try {
                      await deleteScheduledDeal(currentDocId!, deal.documentId!);
                      const updated = await getAllStoreDeals(currentDocId!);
                      setStoreDealsData(updated);
                    } catch (error) {
                      console.error('Error deleting deal:', error);
                    }
                  }
                }}
                onDuplicateDeal={(deal) => {
                  setDealTitle(deal.title ? deal.title + ' (copy)' : '');
                  // Copy images
                  if (deal.imageUrls) {
                    setMobileImageUrl(deal.imageUrls.mobile);
                    setDesktopImageUrl(deal.imageUrls.desktop);
                  } else {
                    setMobileImageUrl(null);
                    setDesktopImageUrl(null);
                  }
                  setMobileImageFile(null);
                  setDesktopImageFile(null);
                  setDeepLinkConfig(deal.deepLink || null);
                  setRegulatoryLink(deal.regulatoryLink || null);
                  setMultiProductIds(deal.deepLink?.productIds || []);
                  // Determine banner link type
                  if (deal.regulatoryLink) {
                    setBannerLinkType('regulatory');
                  } else if (deal.deepLink) {
                    if (deal.deepLink.linkType === 'filter') setBannerLinkType('filter');
                    else if (deal.deepLink.linkType === 'special') setBannerLinkType('special');
                    else if (deal.deepLink.linkType === 'product') setBannerLinkType('product');
                    else if (deal.deepLink.linkType === 'multi-product') setBannerLinkType('multi-product');
                    else setBannerLinkType('none');
                  } else {
                    setBannerLinkType('none');
                  }
                  // Set schedule to NOW → 1 week (using 24-hour format)
                  const now = new Date();
                  const oneWeekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
                  if (currentDocId) {
                    const startDateInStore = convertUtcToStoreTimezone(now, currentDocId);
                    const endDateInStore = convertUtcToStoreTimezone(oneWeekFromNow, currentDocId);
                    setStartDate(getDateInputValue(startDateInStore, currentDocId));
                    setEndDate(getDateInputValue(endDateInStore, currentDocId));
                    setStartTime(getTimeInputValue(startDateInStore, currentDocId));
                    setEndTime(getTimeInputValue(endDateInStore, currentDocId));
                  }
                  setSchedulingEnabled(true);
                  setViewingScheduledDeal(null);
                  setIsEditing(true);
                }}
                onReorderDeals={async (dealOrder) => {
                  if (!currentDocId || !storeDealsData) return;
                  // Optimistically update local state so the list doesn't snap back
                  const orderMap = new Map(dealOrder.map(d => [d.documentId, d.displayOrder]));
                  setStoreDealsData({
                    ...storeDealsData,
                    scheduledDeals: storeDealsData.scheduledDeals.map(deal =>
                      orderMap.has(deal.documentId)
                        ? { ...deal, displayOrder: orderMap.get(deal.documentId) }
                        : deal
                    ),
                  });
                  try {
                    await reorderDeals(currentDocId, dealOrder);
                  } catch (error) {
                    console.error('Error reordering deals:', error);
                    alert(`Failed to reorder deals: ${error instanceof Error ? error.message : 'Unknown error'}`);
                    // Refetch to restore correct order on failure
                    const updated = await getAllStoreDeals(currentDocId);
                    setStoreDealsData(updated);
                  }
                }}
                onScheduleNew={() => {
                  setViewingScheduledDeal(null);
                  setDealTitle('');
                  setMobileImageUrl(null);
                  setDesktopImageUrl(null);
                  setMobileImageFile(null);
                  setDesktopImageFile(null);
                  setDeepLinkConfig(null);
                  setRegulatoryLink(null);
                  setBannerLinkType('none');
                  setStartDate('');
                  setStartTime('00:00'); // Default: midnight
                  setEndDate('');
                  setEndTime('23:59'); // Default: end of day
                  setCustomTimesEnabled(false); // Reset to full-day defaults
                  setSchedulingEnabled(true);
                  setIsEditing(true);
                }}
                onTogglePause={async (deal) => {
                  if (!currentDocId) return;
                  const nextPaused = !deal.isPaused;
                  // Optimistic update so the badge/button flip immediately
                  if (storeDealsData) {
                    setStoreDealsData({
                      ...storeDealsData,
                      scheduledDeals: storeDealsData.scheduledDeals.map(d =>
                        d.documentId === deal.documentId ? { ...d, isPaused: nextPaused } : d
                      ),
                    });
                  }
                  try {
                    await pauseScheduledDeal(currentDocId, deal.documentId, nextPaused);
                    const updated = await getAllStoreDeals(currentDocId);
                    setStoreDealsData(updated);
                  } catch (error) {
                    console.error('Error toggling pause:', error);
                    alert(`Failed to ${nextPaused ? 'pause' : 'resume'} deal: ${error instanceof Error ? error.message : 'Unknown error'}`);
                    // Refetch to reconcile on failure
                    const updated = await getAllStoreDeals(currentDocId);
                    setStoreDealsData(updated);
                  }
                }}
              />
            )}

            {/* Deal Editor Modal - for creating and editing deals */}
            {isEditing && (
              <div className="deal-editor-modal-overlay" onClick={(e) => {
                // Close modal when clicking overlay (not content)
                if (e.target === e.currentTarget) {
                  if (window.confirm('Are you sure you want to close? Any unsaved changes will be lost.')) {
                    setIsEditing(false);
                    setViewingScheduledDeal(null);
                    setDealTitle('');
                    setMobileImageUrl(null);
                    setDesktopImageUrl(null);
                    setMobileImageFile(null);
                    setDesktopImageFile(null);
                    setDeepLinkConfig(null);
                    setRegulatoryLink(null);
                    setBannerLinkType('none');
                    setSchedulingEnabled(false);
                    setScheduleMode('one-time');
                    setRecurringDays([]);
                    setRecurringWeeks(4);
                    setCustomTimesEnabled(false);
                    setCustomStartDateEnabled(false);
                    setRecurringStartDate('');
                    setStartTime('00:00');
                    setEndTime('23:59');
                    // Reset date drift state
                    setDateDriftEnabled(false);
                    setDriftStartHours(0);
                    setDriftEndHours(0);
                  }
                }
              }}>
                <div className="deal-editor-modal">
                  <button
                    className="modal-close-btn"
                    onClick={() => {
                      if (window.confirm('Are you sure you want to close? Any unsaved changes will be lost.')) {
                        setIsEditing(false);
                        setViewingScheduledDeal(null);
                        setDealTitle('');
                        setMobileImageUrl(null);
                        setDesktopImageUrl(null);
                        setMobileImageFile(null);
                        setDesktopImageFile(null);
                        setDeepLinkConfig(null);
                        setRegulatoryLink(null);
                        setBannerLinkType('none');
                        setSchedulingEnabled(false);
                        setScheduleMode('one-time');
                        setRecurringDays([]);
                        setRecurringWeeks(4);
                        setCustomTimesEnabled(false);
                        setCustomStartDateEnabled(false);
                        setRecurringStartDate('');
                        setStartTime('00:00');
                        setEndTime('23:59');
                        // Reset date drift state
                        setDateDriftEnabled(false);
                        setDriftStartHours(0);
                        setDriftEndHours(0);
                      }
                    }}
                    title="Close"
                  >
                    &times;
                  </button>
                  <div className="deal-editor-section">
                <div className="editor-header">
                  <h3>{viewingScheduledDeal && !isEditing ? 'View Scheduled Deal' : (viewingScheduledDeal ? 'Edit Deal' : 'Schedule New Deal')}</h3>
                  {viewingScheduledDeal && !isEditing && (
                    <button onClick={() => setIsEditing(true)} className="edit-deal-btn">
                      Edit Deal
                    </button>
                  )}
                </div>

                {/* Deal Title */}
                <div className="editor-section">
                  <h4>Deal Title</h4>
                  <input
                    type="text"
                    value={dealTitle}
                    onChange={(e) => setDealTitle(e.target.value)}
                    placeholder="e.g. Memorial Day Sale, Weekly Flower Deal"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '2px solid rgba(0, 68, 59, 0.2)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: "'Work Sans', sans-serif",
                      color: '#00443B',
                      background: '#FFFEF1',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                {/* Image Upload */}
                <div className="editor-section">
                  <h4>Banner Images</h4>
                  <ImageUploadManager
                    storeId={currentDocId || ''}
                    mobileImageUrl={mobileImageUrl || undefined}
                    desktopImageUrl={desktopImageUrl || undefined}
                    onMobileImageChange={setMobileImageUrl}
                    onDesktopImageChange={setDesktopImageUrl}
                    onMobileImageFileChange={setMobileImageFile}
                    onDesktopImageFileChange={setDesktopImageFile}
                    schedulingMode={true}
                  />
                </div>

                {/* Banner Link Configuration */}
                <div className="editor-section">
                  <h4>Banner Link (Optional)</h4>
                  <div className="banner-link-selector">
                    <label>
                      <input
                        type="radio"
                        name="bannerLinkType"
                        value="none"
                        checked={bannerLinkType === 'none'}
                        onChange={() => {
                          setBannerLinkType('none');
                          setDeepLinkConfig(null);
                          setRegulatoryLink(null);
                        }}
                        disabled={viewingScheduledDeal !== null && !isEditing}
                      />
                      No Link
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="bannerLinkType"
                        value="filter"
                        checked={bannerLinkType === 'filter'}
                        onChange={() => setBannerLinkType('filter')}
                        disabled={viewingScheduledDeal !== null && !isEditing}
                      />
                      Menu Filter
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="bannerLinkType"
                        value="special"
                        checked={bannerLinkType === 'special'}
                        onChange={() => setBannerLinkType('special')}
                        disabled={viewingScheduledDeal !== null && !isEditing}
                      />
                      Specials Page
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="bannerLinkType"
                        value="multi-product"
                        checked={bannerLinkType === 'multi-product'}
                        onChange={() => setBannerLinkType('multi-product')}
                        disabled={viewingScheduledDeal !== null && !isEditing}
                      />
                      Multiple Products
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="bannerLinkType"
                        value="regulatory"
                        checked={bannerLinkType === 'regulatory'}
                        onChange={() => setBannerLinkType('regulatory')}
                        disabled={viewingScheduledDeal !== null && !isEditing}
                      />
                      External URL
                    </label>
                  </div>

                  {(bannerLinkType === 'filter' || bannerLinkType === 'special') && currentDocId && getStoreContext(currentDocId) && (
                    <DeepLinkConfigurator
                      storeContext={{ ...getStoreContext(currentDocId)!, menuType: 'recreational' }}
                      initialConfig={deepLinkConfig}
                      onChange={setDeepLinkConfig}
                    />
                  )}

                  {bannerLinkType === 'regulatory' && (
                    <RegulatoryLinkInput
                      value={regulatoryLink || ''}
                      onChange={setRegulatoryLink}
                      disabled={viewingScheduledDeal !== null && !isEditing}
                    />
                  )}

                  {bannerLinkType === 'multi-product' && currentDocId && (() => {
                    const ctx = getStoreContext(currentDocId);
                    if (!ctx) return null;
                    const productMap = new Map<string, MenuProduct>();
                    multiProductCatalog.forEach(p => productMap.set(p.id, p));
                    const updateSelection = (ids: string[]) => {
                      setMultiProductIds(ids);
                      setDeepLinkConfig({
                        storeId: ctx.storeId,
                        menuType: ctx.menuType,
                        linkType: 'multi-product',
                        productIds: ids,
                      });
                    };
                    return (
                      <div className="multi-product-picker-section">
                        {multiProductLoading ? (
                          <div className="loading-indicator">Loading products...</div>
                        ) : (
                          <ProductPicker
                            products={multiProductCatalog}
                            productMap={productMap}
                            selectedProductIds={multiProductIds}
                            onAdd={(id) => updateSelection([...multiProductIds, id])}
                            onRemove={(id) => updateSelection(multiProductIds.filter(p => p !== id))}
                            onAddMultiple={(ids) => updateSelection([...multiProductIds, ...ids.filter(i => !multiProductIds.includes(i))])}
                            onRemoveMultiple={(ids) => updateSelection(multiProductIds.filter(p => !ids.includes(p)))}
                            onReorder={(reordered) => updateSelection(reordered)}
                            onClearAll={() => updateSelection([])}
                            label="Selected products"
                            searchPlaceholder="Search products..."
                          />
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Schedule Configuration */}
                <div className="editor-section">
                  <h4>Schedule</h4>
                  {currentDocId && isBrowserTimzoneDifferent(currentDocId) && (
                    <div className="timezone-warning">
                      Note: Your browser is in a different timezone. Times shown are in {getTimezoneAbbreviation(currentDocId)} (store local time).
                    </div>
                  )}

                  {/* Schedule Mode Toggle - only show for new deals */}
                  {!viewingScheduledDeal && (
                    <div className="schedule-mode-toggle" data-testid="schedule-mode-toggle">
                      <label className={`mode-option ${scheduleMode === 'one-time' ? 'active' : ''}`}>
                        <input
                          type="radio"
                          name="scheduleMode"
                          value="one-time"
                          checked={scheduleMode === 'one-time'}
                          onChange={() => setScheduleMode('one-time')}
                          disabled={saving}
                        />
                        <span className="mode-label">One-Time Schedule</span>
                        <span className="mode-desc">- Set specific start and end dates</span>
                      </label>
                      <label className={`mode-option ${scheduleMode === 'recurring' ? 'active' : ''}`}>
                        <input
                          type="radio"
                          name="scheduleMode"
                          value="recurring"
                          checked={scheduleMode === 'recurring'}
                          onChange={() => setScheduleMode('recurring')}
                          disabled={saving}
                        />
                        <span className="mode-label">Recurring Schedule</span>
                        <span className="mode-desc">- Repeat on selected days for multiple weeks</span>
                      </label>
                    </div>
                  )}

                  {/* One-Time Schedule Inputs - show date + time for start and end */}
                  {(scheduleMode === 'one-time' || viewingScheduledDeal) && (
                    <div className="one-time-schedule-section">
                      {/* Date Selection */}
                      <div className="schedule-field-group">
                        <label className="field-group-label">Select Dates</label>
                        <div className="date-inputs-grid">
                          <div className="date-field">
                            <label>Start Date</label>
                            <input
                              type="date"
                              value={startDate}
                              onChange={(e) => setStartDate(e.target.value)}
                              disabled={viewingScheduledDeal !== null && !isEditing}
                            />
                          </div>
                          <div className="date-field">
                            <label>End Date</label>
                            <input
                              type="date"
                              value={endDate}
                              onChange={(e) => setEndDate(e.target.value)}
                              disabled={viewingScheduledDeal !== null && !isEditing}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Time Selection - with full-day default indicator */}
                      <div className="schedule-field-group">
                        <label className="field-group-label">Time Window</label>

                        {/* Full Day Default Indicator */}
                        {!customTimesEnabled && !viewingScheduledDeal && (
                          <div className="full-day-indicator">
                            <div className="full-day-badge">All Day</div>
                            <div className="full-day-content">
                              <span className="full-day-text">Banner displays for full 24 hours</span>
                              <span className="full-day-times">12:00 AM - 11:59 PM</span>
                            </div>
                            <button
                              type="button"
                              className="customize-times-btn"
                              onClick={() => setCustomTimesEnabled(true)}
                            >
                              Set Custom Hours
                            </button>
                          </div>
                        )}

                        {/* Custom Time Inputs */}
                        {(customTimesEnabled || viewingScheduledDeal) && (
                          <div className="time-inputs-section">
                            <div className="time-inputs-header">
                              <span className="time-inputs-title">Custom Hours</span>
                              {!viewingScheduledDeal && (
                                <button
                                  type="button"
                                  className="reset-to-fullday-btn"
                                  onClick={() => {
                                    setCustomTimesEnabled(false);
                                    setStartTime('00:00');
                                    setEndTime('23:59');
                                  }}
                                >
                                  Reset to All Day
                                </button>
                              )}
                            </div>
                            <div className="time-inputs-grid">
                              <div className="time-field">
                                <label>Start Time</label>
                                <input
                                  type="time"
                                  value={startTime}
                                  onChange={(e) => setStartTime(e.target.value)}
                                  disabled={viewingScheduledDeal !== null && !isEditing}
                                />
                              </div>
                              <div className="time-field">
                                <label>End Time</label>
                                <input
                                  type="time"
                                  value={endTime}
                                  onChange={(e) => setEndTime(e.target.value)}
                                  disabled={viewingScheduledDeal !== null && !isEditing}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Recurring Schedule Inputs */}
                  {scheduleMode === 'recurring' && !viewingScheduledDeal && (
                    <div className="recurring-schedule-section">
                      {/* 1. Day Selection - First */}
                      <div className="schedule-field-group">
                        <label className="field-group-label">{dateDriftEnabled ? 'Anchor Days' : 'Repeat On'}</label>
                        {dateDriftEnabled && (
                          <p className="field-group-hint anchor-day-hint">
                            Select the primary days for this deal. The banner will extend before/after these days based on your drift settings.
                          </p>
                        )}
                        {!dateDriftEnabled && (
                          <p className="field-group-hint">Select which days the banner should appear</p>
                        )}
                        <div className="day-checkboxes">
                          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                            <label key={day} className="day-checkbox">
                              <input
                                type="checkbox"
                                checked={recurringDays.includes(index)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setRecurringDays([...recurringDays, index].sort());
                                  } else {
                                    setRecurringDays(recurringDays.filter(d => d !== index));
                                  }
                                }}
                                data-testid={`recurring-day-${index}`}
                              />
                              <span>{day}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* 2. Start Date - with default + override pattern */}
                      <div className="schedule-field-group">
                        <label className="field-group-label">Start Date</label>

                        {/* Default: Next Available Occurrence */}
                        {!customStartDateEnabled && (
                          <div className="full-day-indicator">
                            <div className="full-day-badge">Auto</div>
                            <div className="full-day-content">
                              <span className="full-day-text">Starts from next available occurrence</span>
                              <span className="full-day-times">
                                {recurringDays.length > 0 ? (() => {
                                  // Calculate next occurrence date for display
                                  const today = new Date();
                                  today.setHours(0, 0, 0, 0);
                                  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                  const sortedDays = [...recurringDays].sort((a, b) => a - b);
                                  const currentDay = today.getDay();

                                  // Find next occurrence
                                  let nextDay = sortedDays.find(d => d > currentDay);
                                  if (nextDay === undefined) {
                                    nextDay = sortedDays[0]; // Wrap to next week
                                  }

                                  let daysUntil = nextDay - currentDay;
                                  if (daysUntil <= 0) daysUntil += 7;

                                  const nextDate = new Date(today);
                                  nextDate.setDate(nextDate.getDate() + daysUntil);

                                  return `Next ${dayNames[nextDay]}, ${nextDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                                })() : 'Select days above'}
                              </span>
                            </div>
                            <button
                              type="button"
                              className="customize-times-btn"
                              onClick={() => setCustomStartDateEnabled(true)}
                            >
                              Set Custom Start Date
                            </button>
                          </div>
                        )}

                        {/* Custom Start Date */}
                        {customStartDateEnabled && (
                          <div className="time-inputs-section">
                            <div className="time-inputs-header">
                              <span className="time-inputs-title">Custom Start Date</span>
                              <button
                                type="button"
                                className="reset-to-fullday-btn"
                                onClick={() => {
                                  setCustomStartDateEnabled(false);
                                  setRecurringStartDate('');
                                }}
                              >
                                Reset to Auto
                              </button>
                            </div>
                            <div className="date-input-wrapper">
                              <input
                                type="date"
                                value={recurringStartDate}
                                onChange={(e) => setRecurringStartDate(e.target.value)}
                                min={new Date().toISOString().split('T')[0]}
                                disabled={saving}
                                data-testid="recurring-start-date"
                              />
                              <p className="field-group-hint" style={{ marginTop: '8px' }}>
                                Schedule will start from this date forward
                              </p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* 3. Duration */}
                      <div className="schedule-field-group">
                        <label className="field-group-label">Duration</label>
                        <p className="field-group-hint">How many weeks to repeat this schedule</p>
                        <select
                          value={recurringWeeks}
                          onChange={(e) => setRecurringWeeks(parseInt(e.target.value))}
                          className="weeks-select"
                          data-testid="recurring-weeks-select"
                        >
                          {[1, 2, 3, 4, 5, 6, 7, 8].map(w => (
                            <option key={w} value={w}>{w} week{w > 1 ? 's' : ''}</option>
                          ))}
                          <option value={13}>90 days (~13 weeks)</option>
                        </select>
                      </div>

                      {/* 4. Date Drift - Extended Duration */}
                      <div className="schedule-field-group">
                        <label className="field-group-label">Extended Duration</label>

                        {/* Toggle */}
                        <div className="drift-toggle">
                          <label className="drift-toggle-label">
                            <input
                              type="checkbox"
                              checked={dateDriftEnabled}
                              onChange={(e) => {
                                setDateDriftEnabled(e.target.checked);
                                if (!e.target.checked) {
                                  setDriftStartHours(0);
                                  setDriftEndHours(0);
                                }
                              }}
                              disabled={saving}
                              data-testid="drift-enabled-checkbox"
                            />
                            <span>Enable Date Drift</span>
                          </label>
                          <p className="field-group-hint">
                            Allow the banner to start before or end after the anchor day
                          </p>
                        </div>

                        {/* Drift Controls - only show when enabled */}
                        {dateDriftEnabled && (
                          <div className="drift-controls">
                            <div className="drift-info-banner">
                              Date drift extends your banner beyond the anchor day.
                              For example, a Wednesday deal with 6-hour drift could run from Tuesday 6pm to Thursday 6am.
                            </div>

                            <div className="drift-inputs">
                              <div className="drift-input-group">
                                <label>Start Earlier By</label>
                                <select
                                  value={driftStartHours}
                                  onChange={(e) => setDriftStartHours(parseInt(e.target.value))}
                                  disabled={saving}
                                  data-testid="drift-start-hours"
                                >
                                  <option value={0}>No drift</option>
                                  {[1,2,3,4,5,6,7,8,9,10,11,12].map(h => (
                                    <option key={h} value={h}>{h} hour{h > 1 ? 's' : ''}</option>
                                  ))}
                                </select>
                                <span className="drift-hint">before anchor day</span>
                              </div>

                              <div className="drift-input-group">
                                <label>End Later By</label>
                                <select
                                  value={driftEndHours}
                                  onChange={(e) => setDriftEndHours(parseInt(e.target.value))}
                                  disabled={saving}
                                  data-testid="drift-end-hours"
                                >
                                  <option value={0}>No drift</option>
                                  {[1,2,3,4,5,6,7,8,9,10,11,12].map(h => (
                                    <option key={h} value={h}>{h} hour{h > 1 ? 's' : ''}</option>
                                  ))}
                                </select>
                                <span className="drift-hint">after anchor day ends</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* 5. Daily Time Window */}
                      <div className="schedule-field-group">
                        <label className="field-group-label">Daily Time Window</label>

                        {/* Full Day Default Indicator */}
                        {!customTimesEnabled && (
                          <div className="full-day-indicator">
                            <div className="full-day-badge">All Day</div>
                            <div className="full-day-content">
                              <span className="full-day-text">Banner displays for full 24 hours each day</span>
                              <span className="full-day-times">12:00 AM - 11:59 PM</span>
                            </div>
                            <button
                              type="button"
                              className="customize-times-btn"
                              onClick={() => setCustomTimesEnabled(true)}
                            >
                              Set Custom Hours
                            </button>
                          </div>
                        )}

                        {/* Custom Time Inputs */}
                        {customTimesEnabled && (
                          <div className="time-inputs-section">
                            <div className="time-inputs-header">
                              <span className="time-inputs-title">Custom Hours</span>
                              <button
                                type="button"
                                className="reset-to-fullday-btn"
                                onClick={() => {
                                  setCustomTimesEnabled(false);
                                  setStartTime('00:00');
                                  setEndTime('23:59');
                                }}
                              >
                                Reset to All Day
                              </button>
                            </div>
                            <div className="time-inputs-grid">
                              <div className="time-field">
                                <label>Start Time</label>
                                <input
                                  type="time"
                                  value={startTime}
                                  onChange={(e) => setStartTime(e.target.value)}
                                  disabled={saving}
                                  data-testid="recurring-start-time"
                                />
                              </div>
                              <div className="time-field">
                                <label>End Time</label>
                                <input
                                  type="time"
                                  value={endTime}
                                  onChange={(e) => setEndTime(e.target.value)}
                                  disabled={saving}
                                  data-testid="recurring-end-time"
                                />
                              </div>
                            </div>
                            {startTime && endTime && (() => {
                              const [startHours] = startTime.split(':').map(Number);
                              const [endHours] = endTime.split(':').map(Number);
                              const isOvernight = endHours < startHours;
                              return isOvernight ? (
                                <div className="overnight-indicator">
                                  Overnight schedule - banner ends the following day
                                </div>
                              ) : null;
                            })()}
                          </div>
                        )}
                      </div>

                      {/* Recurring Preview */}
                      {recurringDays.length > 0 && startTime && endTime && currentDocId && (() => {
                        try {
                          const customDate = customStartDateEnabled && recurringStartDate ? recurringStartDate : undefined;
                          // Include drift config in preview calculation
                          const previewDriftConfig = dateDriftEnabled && (driftStartHours > 0 || driftEndHours > 0)
                            ? { startHoursBefore: driftStartHours, endHoursAfter: driftEndHours }
                            : undefined;
                          const recurringDates = generateRecurringDates(startTime, endTime, recurringDays, recurringWeeks, currentDocId, customDate, previewDriftConfig);
                          const [startHours] = startTime.split(':').map(Number);
                          const [endHours] = endTime.split(':').map(Number);
                          const isOvernight = endHours < startHours;
                          // Show full date range if overnight OR if drift is enabled (drift can also span days)
                          const showFullDateRange = isOvernight || dateDriftEnabled;
                          return (
                            <div className="recurring-preview">
                              <strong>Will create {recurringDates.length} banners{dateDriftEnabled ? ' (with date drift)' : ''}:</strong>
                              <div className="preview-dates">
                                {recurringDates
                                  .slice(0, 5)
                                  .map((d, i) => (
                                    <div key={i} className="preview-date">
                                      {d.startDateTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                      {' '}
                                      {d.startDateTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                      {showFullDateRange && (
                                        <> → {d.endDateTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                        {' '}
                                        {d.endDateTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</>
                                      )}
                                      {!showFullDateRange && (
                                        <> - {d.endDateTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</>
                                      )}
                                    </div>
                                  ))}
                                {recurringDates.length > 5 && (
                                  <div className="preview-more">+{recurringDates.length - 5} more</div>
                                )}
                              </div>
                            </div>
                          );
                        } catch (e) {
                          return null; // Don't crash on preview errors
                        }
                      })()}

                      {recurringDays.length === 0 && (
                        <div className="recurring-hint">
                          Select at least one day to create recurring banners
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Menu Type Selector - Publish to Med/Rec/Both */}
                {!viewingScheduledDeal && currentStorePath && (() => {
                  const pairedIds = getPairedMenuIds(currentStorePath);
                  const hasBothMenus = !!(pairedIds.medical && pairedIds.recreational);

                  // Only show selector if store has both medical and recreational menus
                  if (!hasBothMenus) return null;

                  return (
                    <div className="editor-section">
                      <h4>Publish To</h4>
                      <div className="menu-type-selector" data-testid="menu-type-selector">
                        <div className="menu-type-options">
                          <label className="menu-type-option">
                            <input
                              type="checkbox"
                              data-testid="menu-type-medical"
                              checked={targetMenuTypes.includes('medical')}
                              onChange={() => {
                                setTargetMenuTypes(prev =>
                                  prev.includes('medical')
                                    ? prev.filter(t => t !== 'medical')
                                    : [...prev, 'medical']
                                );
                              }}
                              disabled={saving}
                            />
                            <span>Medical</span>
                          </label>
                          <label className="menu-type-option">
                            <input
                              type="checkbox"
                              data-testid="menu-type-recreational"
                              checked={targetMenuTypes.includes('recreational')}
                              onChange={() => {
                                setTargetMenuTypes(prev =>
                                  prev.includes('recreational')
                                    ? prev.filter(t => t !== 'recreational')
                                    : [...prev, 'recreational']
                                );
                              }}
                              disabled={saving}
                            />
                            <span>Recreational</span>
                          </label>
                        </div>
                        {targetMenuTypes.length === 0 && (
                          <div className="menu-type-warning">
                            Please select at least one menu type
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Actions */}
                <div className="editor-actions">
                  <button
                    onClick={() => {
                      setViewingScheduledDeal(null);
                      setIsEditing(false);
                      setDealTitle('');
                      setMobileImageUrl(null);
                      setDesktopImageUrl(null);
                      setMobileImageFile(null);
                      setDesktopImageFile(null);
                      setDeepLinkConfig(null);
                      setRegulatoryLink(null);
                      setBannerLinkType('none');
                      setSchedulingEnabled(false);
                      setScheduleMode('one-time');
                      setRecurringDays([]);
                      setRecurringWeeks(4);
                      // Reset date drift state
                      setDateDriftEnabled(false);
                      setDriftStartHours(0);
                      setDriftEndHours(0);
                    }}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                  {(isEditing || !viewingScheduledDeal) && (
                    <button
                      onClick={async () => {
                        // Validation
                        if (!viewingScheduledDeal && !dealTitle.trim()) {
                          alert('Please enter a deal title');
                          return;
                        }
                        if (!desktopImageUrl && !desktopImageFile) {
                          alert('Please upload a banner image');
                          return;
                        }
                        // Validation differs based on schedule mode
                        const isRecurring = scheduleMode === 'recurring';

                        if (isRecurring) {
                          // Recurring mode: require times and at least one day
                          if (!startTime || !endTime) {
                            alert('Please set both start and end times');
                            return;
                          }
                          if (recurringDays.length === 0) {
                            alert('Please select at least one day of the week');
                            return;
                          }
                        } else {
                          // One-time mode: require dates and times
                          if (!startDate || !startTime || !endDate || !endTime) {
                            alert('Please set both start and end dates/times');
                            return;
                          }
                        }

                        // For new deals, validate menu type selection
                        if (!viewingScheduledDeal && currentStorePath) {
                          const pairedIds = getPairedMenuIds(currentStorePath);
                          const hasBothMenus = !!(pairedIds.medical && pairedIds.recreational);
                          if (hasBothMenus && targetMenuTypes.length === 0) {
                            alert('Please select at least one menu type (Medical or Recreational)');
                            return;
                          }
                        }

                        // For recurring, we use placeholder dates since generateRecurringDates handles actual dates
                        // For one-time, use the actual entered dates
                        const effectiveStartDate = isRecurring ? new Date().toISOString().split('T')[0] : startDate;
                        const effectiveEndDate = isRecurring ? new Date().toISOString().split('T')[0] : endDate;

                        const startDateTime = combineDateTime(effectiveStartDate, startTime, currentDocId!);
                        const endDateTime = combineDateTime(effectiveEndDate, endTime, currentDocId!);

                        if (!startDateTime || !endDateTime) {
                          alert('Invalid date/time values');
                          return;
                        }

                        // Check if this is an overnight schedule (for recurring deals)
                        const [startHours] = startTime.split(':').map(Number);
                        const [endHours] = endTime.split(':').map(Number);
                        const isOvernightSchedule = isRecurring && endHours < startHours;

                        // For non-recurring deals, end must be after start
                        // For recurring overnight deals, this is expected (generateRecurringDates handles it)
                        if (!isRecurring && endDateTime <= startDateTime) {
                          alert('End time must be after start time');
                          return;
                        }

                        try {
                          setSaving(true);

                          // Prepare deep link URL if configured
                          let deepLinkUrl: string | undefined;
                          if (bannerLinkType === 'filter' && deepLinkConfig) {
                            const env: Environment = window.location.hostname.includes('localhost') ? 'development' : 'production';
                            deepLinkUrl = generateDeepLinkUrl(deepLinkConfig, env);
                          } else if (bannerLinkType === 'special') {
                            const env: Environment = window.location.hostname.includes('localhost') ? 'development' : 'production';
                            deepLinkUrl = generateDeepLinkUrl({ linkType: 'special' }, env);
                          } else if (bannerLinkType === 'multi-product' && deepLinkConfig) {
                            const env: Environment = window.location.hostname.includes('localhost') ? 'development' : 'production';
                            deepLinkUrl = generateDeepLinkUrl(deepLinkConfig, env);
                          }

                          // Block save if multi-product was selected but no products picked
                          if (bannerLinkType === 'multi-product' && multiProductIds.length === 0) {
                            alert('Pick at least one product, or change the link type.');
                            setSaving(false);
                            return;
                          }

                          let dealsCreatedCount = 1; // Default for updates

                          if (viewingScheduledDeal?.documentId) {
                            // Update existing deal - only update the single deal
                            // Override menuType in deepLink config to match the target menu
                            let updateDeepLinkConfig = deepLinkConfig;
                            if (deepLinkConfig && bannerLinkType !== 'none' && bannerLinkType !== 'regulatory') {
                              const menuStoreContext = getStoreContext(currentDocId!);
                              if (menuStoreContext) {
                                updateDeepLinkConfig = { ...deepLinkConfig, menuType: menuStoreContext.menuType };
                              }
                            }

                            // Save logic for an existing deal, parameterized by scope.
                            // 'this'              → single-doc update (preserves date overlap behavior)
                            // 'future' | 'all'    → bulk update via the series endpoint
                            const performUpdate = async (scope: SeriesScope) => {
                              if (scope === 'this') {
                                await updateScheduledImageDeal(
                                  currentDocId!,
                                  viewingScheduledDeal.documentId,
                                  {
                                    mobileImage: mobileImageFile || undefined,
                                    desktopImage: desktopImageFile || undefined,
                                    existingMobileUrl: mobileImageFile ? undefined : mobileImageUrl || undefined,
                                    existingDesktopUrl: desktopImageFile ? undefined : desktopImageUrl || undefined,
                                    startDateTime,
                                    endDateTime,
                                    deepLink: bannerLinkType !== 'none' && bannerLinkType !== 'regulatory' ? updateDeepLinkConfig || undefined : undefined,
                                    deepLinkUrl,
                                    regulatoryLink: bannerLinkType === 'regulatory' ? regulatoryLink || undefined : undefined,
                                    title: dealTitle.trim() || undefined,
                                  }
                                );
                                return;
                              }

                              // Bulk path: if the user uploaded a new image, upload once
                              // (instead of re-uploading per instance) and propagate the URLs.
                              let bulkImageUrls: { mobile: string; desktop: string } | undefined;
                              if (mobileImageFile && desktopImageFile) {
                                const [mobileResult, desktopResult] = await Promise.all([
                                  uploadMobileImage(currentDocId!, mobileImageFile),
                                  uploadDesktopImage(currentDocId!, desktopImageFile),
                                ]);
                                bulkImageUrls = {
                                  mobile: mobileResult.imageUrl,
                                  desktop: desktopResult.imageUrl,
                                };
                              } else if (mobileImageUrl && desktopImageUrl) {
                                // No new file but the deal already had images — re-broadcast existing URLs
                                bulkImageUrls = { mobile: mobileImageUrl, desktop: desktopImageUrl };
                              }

                              await updateScheduledDealSeries(
                                currentDocId!,
                                viewingScheduledDeal.recurringSeriesId!,
                                {
                                  scope,
                                  instanceDocumentId: viewingScheduledDeal.documentId,
                                  title: dealTitle.trim() || undefined,
                                  imageUrls: bulkImageUrls,
                                  deepLink: bannerLinkType !== 'none' && bannerLinkType !== 'regulatory' ? (updateDeepLinkConfig || null) : null,
                                  regulatoryLink: bannerLinkType === 'regulatory' ? (regulatoryLink || null) : null,
                                }
                              );
                            };

                            if (viewingScheduledDeal.recurringSeriesId) {
                              // Defer to the scope-choice dialog. We exit this onClick early;
                              // the dialog's onConfirm will pick up from here with the chosen scope.
                              setSeriesScopeDialog({
                                action: 'edit',
                                deal: viewingScheduledDeal,
                                commit: async (scope) => {
                                  await performUpdate(scope);
                                  const updated = await getAllStoreDeals(currentDocId!);
                                  setStoreDealsData(updated);
                                  setViewingScheduledDeal(null);
                                  setIsEditing(false);
                                },
                              });
                              setSaving(false);
                              return;
                            }

                            await performUpdate('this');
                          } else {
                            // Create new deal(s) - handle multi-menu and recurring
                            const pairedIds = currentStorePath ? getPairedMenuIds(currentStorePath) : {};
                            const hasBothMenus = !!(pairedIds.medical && pairedIds.recreational);

                            // Determine which menu IDs to create deals for
                            let menuIdsToCreate: string[] = [];
                            if (hasBothMenus) {
                              if (targetMenuTypes.includes('medical') && pairedIds.medical) {
                                menuIdsToCreate.push(pairedIds.medical);
                              }
                              if (targetMenuTypes.includes('recreational') && pairedIds.recreational) {
                                menuIdsToCreate.push(pairedIds.recreational);
                              }
                            } else {
                              // Single menu store - use the current doc ID
                              menuIdsToCreate = [currentDocId!];
                            }

                            // Generate dates for recurring or single deal
                            let datesToCreate: Array<{ startDateTime: Date; endDateTime: Date }> = [];
                            if (isRecurring && recurringDays.length > 0) {
                              const customDate = customStartDateEnabled && recurringStartDate ? recurringStartDate : undefined;
                              // Build drift config if enabled
                              const driftConfig = dateDriftEnabled && (driftStartHours > 0 || driftEndHours > 0)
                                ? { startHoursBefore: driftStartHours, endHoursAfter: driftEndHours }
                                : undefined;
                              datesToCreate = generateRecurringDates(
                                startTime, endTime,
                                recurringDays, recurringWeeks, currentDocId!, customDate, driftConfig
                              );
                            } else {
                              datesToCreate = [{ startDateTime, endDateTime }];
                            }

                            // Calculate total deals to create
                            const totalDeals = menuIdsToCreate.length * datesToCreate.length;
                            dealsCreatedCount = totalDeals;

                            // Confirm if creating multiple deals
                            if (totalDeals > 1) {
                              const menuTypeDesc = menuIdsToCreate.length > 1 ? 'Medical and Recreational' :
                                (targetMenuTypes.includes('medical') ? 'Medical' : 'Recreational');
                              const confirmMsg = isRecurring
                                ? `This will create ${totalDeals} deals (${datesToCreate.length} dates × ${menuIdsToCreate.length} menu${menuIdsToCreate.length > 1 ? 's' : ''}). Continue?`
                                : `This will create ${totalDeals} deals for ${menuTypeDesc}. Continue?`;

                              if (!window.confirm(confirmMsg)) {
                                setSaving(false);
                                return;
                              }
                            }

                            // Pre-resolve specialIds per menu for multi-menu special links
                            let specialIdByMenu: Record<string, string> = {};
                            if (deepLinkConfig?.linkType === 'special' && deepLinkConfig.specialId && menuIdsToCreate.length > 1) {
                              const allMenuSpecials = await Promise.all(
                                menuIdsToCreate.map(async (menuId) => ({
                                  menuId,
                                  specials: await fetchSpecialsForMenu(menuId)
                                }))
                              );
                              const sourceSpecials = allMenuSpecials.find(m => m.menuId === currentDocId);
                              const selectedSpecial = sourceSpecials?.specials.find(s => s.id === deepLinkConfig.specialId);
                              if (selectedSpecial) {
                                for (const { menuId, specials } of allMenuSpecials) {
                                  const match = specials.find(s => s.name === selectedSpecial.name);
                                  if (match) specialIdByMenu[menuId] = match.id;
                                }
                              }
                            }

                            // For recurring creates that span multiple menus and/or dates,
                            // tag every generated instance with a shared series id so we
                            // can later bulk-edit/delete the whole series.
                            const seriesId = isRecurring ? crypto.randomUUID() : undefined;

                            // Create all the deals
                            for (const menuId of menuIdsToCreate) {
                              // Override menuType and specialId in deepLink config to match the target menu
                              let menuDeepLinkConfig = deepLinkConfig;
                              if (deepLinkConfig && bannerLinkType !== 'none' && bannerLinkType !== 'regulatory') {
                                const menuStoreContext = getStoreContext(menuId);
                                if (menuStoreContext) {
                                  menuDeepLinkConfig = {
                                    ...deepLinkConfig,
                                    menuType: menuStoreContext.menuType,
                                    ...(specialIdByMenu[menuId] ? { specialId: specialIdByMenu[menuId] } : {})
                                  };
                                }
                              }

                              for (const { startDateTime: start, endDateTime: end } of datesToCreate) {
                                await createScheduledImageDeal(
                                  menuId,
                                  mobileImageFile!,
                                  desktopImageFile!,
                                  start,
                                  end,
                                  menuDeepLinkConfig && bannerLinkType !== 'none' && bannerLinkType !== 'regulatory' ? menuDeepLinkConfig : undefined,
                                  bannerLinkType === 'regulatory' ? regulatoryLink : undefined,
                                  dealTitle.trim() || undefined,
                                  seriesId
                                );
                              }
                            }
                          }

                          // Refresh deals data
                          const updated = await getAllStoreDeals(currentDocId!);
                          setStoreDealsData(updated);

                          // Reset editor state
                          setViewingScheduledDeal(null);
                          setIsEditing(false);
                          setDealTitle('');
                          setMobileImageUrl(null);
                          setDesktopImageUrl(null);
                          setMobileImageFile(null);
                          setDesktopImageFile(null);
                          setDeepLinkConfig(null);
                          setRegulatoryLink(null);
                          setBannerLinkType('none');
                          setSchedulingEnabled(false);
                          setScheduleMode('one-time');
                          setRecurringDays([]);
                          setRecurringWeeks(4);
                          // Reset date drift state
                          setDateDriftEnabled(false);
                          setDriftStartHours(0);
                          setDriftEndHours(0);

                          alert(viewingScheduledDeal ? 'Deal updated successfully!' : `${dealsCreatedCount} deal${dealsCreatedCount > 1 ? 's' : ''} scheduled successfully!`);
                        } catch (error) {
                          console.error('Error saving deal:', error);
                          alert(`Failed to save deal: ${error instanceof Error ? error.message : 'Unknown error'}`);
                        } finally {
                          setSaving(false);
                        }
                      }}
                      disabled={saving || (!viewingScheduledDeal && currentStorePath && getPairedMenuIds(currentStorePath).medical && getPairedMenuIds(currentStorePath).recreational && targetMenuTypes.length === 0)}
                      className="save-btn"
                    >
                      {saving ? 'Saving...' : (viewingScheduledDeal ? 'Update Deal' : 'Schedule Deal')}
                    </button>
                  )}
                </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        );

      case 'store-hours':
        return (
          <div className="admin-feature-content">
            {storeHoursData && hours && (
              <>
                <StoreHoursEditor hours={hours} onChange={setHours} />
                <TemporaryClosuresManager
                  temporaryClosures={temporaryClosures}
                  onChange={setTemporaryClosures}
                  storeId={currentDocId || ''}
                />
                <div className="admin-actions">
                  <button onClick={handleSaveHours} disabled={saving} className="save-button">
                    {saving ? 'Saving...' : 'Save Hours & Closures'}
                  </button>
                </div>
              </>
            )}
            {showConfirmation && originalHours && hours && selectedStore && (
              <StoreChangesConfirmation
                storeName={`${selectedStore.city}, ${selectedStore.state}`}
                oldHours={originalHours}
                newHours={hours}
                oldClosures={originalTemporaryClosures}
                newClosures={temporaryClosures}
                onConfirm={handleConfirmSave}
                onCancel={() => setShowConfirmation(false)}
              />
            )}
          </div>
        );

      case 'menu-audit':
        return (
          <div className="admin-feature-content">
            {menuAuditProducts.length > 0 && (
              <MenuAuditTable
                products={menuAuditProducts}
                storeName={`${selectedStore.city} ${selectedMenuType?.toUpperCase()}`}
                menuType={selectedMenuType === 'med' ? 'Medical' : 'Recreational'}
              />
            )}
          </div>
        );

      case 'home-collections': {
        const storeConfigKey = `${selectedStore.stateFull}/${selectedStore.id}`;
        const storeConfig = STORE_MENU_CONFIG[storeConfigKey];
        const menuConfig = storeConfig?.[selectedMenuType === 'med' ? 'medical' : 'recreational'];
        const storeName = selectedStore.id;
        const menuTypeValue = selectedMenuType || 'rec';

        return (
          <div className="admin-feature-content">
            {menuConfig?.menuId ? (
              <HomeCollectionsEditor
                storeName={storeName}
                menuType={menuTypeValue}
                displayName={`${selectedStore.city} ${menuTypeValue.toUpperCase()}`}
                retailerId={menuConfig.menuId}
                legacyMenuId={menuConfig.legacyMenuId}
                onBack={() => setSelectedFeature('deals')}
              />
            ) : (
              <p>No menu configuration found for this store.</p>
            )}
          </div>
        );
      }

      case 'popup-alerts':
        return (
          <div className="admin-feature-content">
            <PopupAlertEditor storeId={selectedStore?.id || ''} />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="admin-portal-redesign" data-testid="admin-portal">
      <Helmet>
        <title>Admin Portal - Sweetspot</title>
        <meta name="robots" content="noindex, nofollow, noarchive, nosnippet" />
      </Helmet>

      <AdminHeader
        stores={stores}
        selectedStore={selectedStore}
        selectedMenuType={selectedMenuType}
        onSelectStore={handleStoreSelect}
        userEmail={user?.email}
        onLogout={handleLogout}
      />

      <main className="admin-main">
        {/* Show feature selector when store is selected (for deals) or when menu type is also selected (for other features) */}
        {selectedStore && (selectedFeature === 'deals' || selectedFeature === 'popup-alerts' || (selectedMenuType && !needsMenuTypeSelection)) && (
          <AdminFeatureSelector
            selectedFeature={selectedFeature}
            onSelectFeature={handleFeatureChange}
          />
        )}

        <div className="admin-content-area">
          {renderContent()}
        </div>
      </main>

      <SeriesScopeDialog
        open={seriesScopeDialog !== null}
        action={seriesScopeDialog?.action ?? 'edit'}
        onCancel={() => setSeriesScopeDialog(null)}
        onConfirm={async (scope) => {
          if (!seriesScopeDialog || !currentDocId) return;
          const dialog = seriesScopeDialog;
          setSeriesScopeDialog(null);
          try {
            if (dialog.action === 'delete') {
              if (scope === 'this') {
                await deleteScheduledDeal(currentDocId, dialog.deal.documentId);
              } else {
                await deleteScheduledDealSeries(currentDocId, dialog.deal.recurringSeriesId!, {
                  scope,
                  instanceDocumentId: dialog.deal.documentId,
                });
              }
              const updated = await getAllStoreDeals(currentDocId);
              setStoreDealsData(updated);
            } else if (dialog.action === 'edit') {
              await dialog.commit(scope);
            }
          } catch (error) {
            console.error('Series-scope action failed:', error);
            alert(`Action failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
          }
        }}
      />
    </div>
  );
};

export default AdminPortalRedesign;
